import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Card,
  Typography,
  message,
  InputNumber,
  DatePicker,
  Space,
  Divider,
  Table,
  Modal,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Customer, Vehicle, WorkOrder, WorkOrderPart, WorkOrderLabor } from '../../types';
import { customerApi, vehicleApi } from '../../services/api';
import PageRefreshWarning from '../PageRefreshWarning';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface WorkOrderFormProps {
  workOrder?: WorkOrder;
  onSuccess?: (workOrder: WorkOrder) => void;
  onCancel?: () => void;
  modal?: boolean;
}

const WorkOrderForm: React.FC<WorkOrderFormProps> = ({
  workOrder,
  onSuccess,
  onCancel,
  modal = false,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    workOrder?.customer_id || null
  );
  const [parts, setParts] = useState<WorkOrderPart[]>(workOrder?.parts_needed || []);
  const [laborItems, setLaborItems] = useState<WorkOrderLabor[]>(workOrder?.labor_items || []);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isEditing = Boolean(workOrder);

  useEffect(() => {
    fetchCustomers();
    if (workOrder) {
      setSelectedCustomerId(workOrder.customer_id);
      form.setFieldsValue({
        ...workOrder,
        estimated_completion: workOrder.estimated_completion ? dayjs(workOrder.estimated_completion) : null,
      });
    }
  }, [workOrder, form]);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchCustomerVehicles(selectedCustomerId);
    } else {
      setVehicles([]);
    }
  }, [selectedCustomerId]);

  const fetchCustomers = async () => {
    try {
      const response = await customerApi.getAll();
      setCustomers(response.data);
    } catch (error) {
      message.error('Failed to fetch customers');
    }
  };

  const fetchCustomerVehicles = async (customerId: number) => {
    try {
      const response = await vehicleApi.getByCustomerId(customerId);
      setVehicles(response.data);
    } catch (error) {
      message.error('Failed to fetch vehicles');
    }
  };

  const calculateTotalAmount = () => {
    const partsTotal = parts.reduce((sum, part) => sum + part.total_price, 0);
    const laborTotal = laborItems.reduce((sum, labor) => sum + labor.total_price, 0);
    return partsTotal + laborTotal;
  };

  const generateWorkOrderNumber = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `WO${year}${month}${day}-${random}`;
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const totalAmount = calculateTotalAmount();
      const workOrderData: Partial<WorkOrder> = {
        ...values,
        parts_needed: parts,
        labor_items: laborItems,
        total_amount: totalAmount,
        wo_number: workOrder?.wo_number || generateWorkOrderNumber(),
        estimated_completion: values.estimated_completion?.toISOString(),
      };

      if (isEditing && workOrder?.id) {
        // Update existing work order
        // await workOrderApi.update(workOrder.id, workOrderData);
        message.success('Work order updated successfully', 3);
      } else {
        // Create new work order
        // await workOrderApi.create(workOrderData);
        message.success('Work order created successfully', 3);
      }

      setHasUnsavedChanges(false);
      onSuccess?.(workOrderData as WorkOrder);
    } catch (error) {
      message.error('Failed to save work order. Please try again.', 5);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/admin/work-orders');
    }
  };

  const addPart = () => {
    const newPart: WorkOrderPart = {
      work_order_id: workOrder?.id || 0,
      part_name: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      status: 'needed',
    };
    setParts([...parts, newPart]);
  };

  const updatePart = (index: number, field: keyof WorkOrderPart, value: any) => {
    const updatedParts = [...parts];
    updatedParts[index] = { ...updatedParts[index], [field]: value };

    if (field === 'quantity' || field === 'unit_price') {
      updatedParts[index].total_price = updatedParts[index].quantity * updatedParts[index].unit_price;
    }

    setParts(updatedParts);
    setHasUnsavedChanges(true);
  };

  const removePart = (index: number) => {
    setParts(parts.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  const addLabor = () => {
    const newLabor: WorkOrderLabor = {
      work_order_id: workOrder?.id || 0,
      description: '',
      hours: 1,
      hourly_rate: 75,
      total_price: 75,
    };
    setLaborItems([...laborItems, newLabor]);
    setHasUnsavedChanges(true);
  };

  const updateLabor = (index: number, field: keyof WorkOrderLabor, value: any) => {
    const updatedLabor = [...laborItems];
    updatedLabor[index] = { ...updatedLabor[index], [field]: value };

    if (field === 'hours' || field === 'hourly_rate') {
      updatedLabor[index].total_price = updatedLabor[index].hours * updatedLabor[index].hourly_rate;
    }

    setLaborItems(updatedLabor);
    setHasUnsavedChanges(true);
  };

  const removeLabor = (index: number) => {
    setLaborItems(laborItems.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  const partsColumns = [
    {
      title: 'Part Name',
      dataIndex: 'part_name',
      key: 'part_name',
      render: (text: string, record: WorkOrderPart, index: number) => (
        <Input
          value={text}
          onChange={(e) => updatePart(index, 'part_name', e.target.value)}
          placeholder="Enter part name"
        />
      ),
    },
    {
      title: 'Part #',
      dataIndex: 'part_number',
      key: 'part_number',
      width: 120,
      render: (text: string, record: WorkOrderPart, index: number) => (
        <Input
          value={text}
          onChange={(e) => updatePart(index, 'part_number', e.target.value)}
          placeholder="Part #"
        />
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      render: (value: number, record: WorkOrderPart, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updatePart(index, 'quantity', val || 0)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 100,
      render: (value: number, record: WorkOrderPart, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updatePart(index, 'unit_price', val || 0)}
          min={0}
          formatter={(value) => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 100,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string, record: WorkOrderPart, index: number) => (
        <Select
          value={status}
          onChange={(val) => updatePart(index, 'status', val)}
          style={{ width: '100%' }}
        >
          <Option value="needed">Needed</Option>
          <Option value="ordered">Ordered</Option>
          <Option value="received">Received</Option>
          <Option value="installed">Installed</Option>
        </Select>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 60,
      render: (text: any, record: WorkOrderPart, index: number) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removePart(index)}
          danger
        />
      ),
    },
  ];

  const laborColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: WorkOrderLabor, index: number) => (
        <Input
          value={text}
          onChange={(e) => updateLabor(index, 'description', e.target.value)}
          placeholder="Labor description"
        />
      ),
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      width: 100,
      render: (value: number, record: WorkOrderLabor, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updateLabor(index, 'hours', val || 0)}
          min={0}
          step={0.5}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Rate',
      dataIndex: 'hourly_rate',
      key: 'hourly_rate',
      width: 100,
      render: (value: number, record: WorkOrderLabor, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updateLabor(index, 'hourly_rate', val || 0)}
          min={0}
          formatter={(value) => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 100,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Action',
      key: 'action',
      width: 60,
      render: (text: any, record: WorkOrderLabor, index: number) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removeLabor(index)}
          danger
        />
      ),
    },
  ];

  return (
    <>
      <PageRefreshWarning enabled={hasUnsavedChanges && !loading} />
      <div className={modal ? '' : 'p-6 max-w-6xl mx-auto'}>
      {!modal && (
        <div className="flex items-center gap-4 mb-6">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-800"
          >
            Back
          </Button>
          <div>
            <Title level={2} className="mb-0">
              {isEditing ? 'Edit Work Order' : 'New Work Order'}
            </Title>
            <p className="text-gray-600">
              {isEditing ? 'Update work order details' : 'Create a new repair job'}
            </p>
          </div>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        size="large"
      >
        <Row gutter={[24, 24]}>
          {/* Customer and Vehicle Selection */}
          <Col xs={24}>
            <Card title="Customer & Vehicle" className="mb-6">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="customer_id"
                    label="Customer"
                    rules={[{ required: true, message: 'Please select a customer' }]}
                  >
                    <Select
                      placeholder="Select customer"
                      showSearch
                      optionFilterProp="children"
                      onChange={setSelectedCustomerId}
                      disabled={isEditing}
                    >
                      {customers.map((customer) => (
                        <Option key={customer.id} value={customer.id}>
                          {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="vehicle_id"
                    label="Vehicle"
                    rules={[{ required: true, message: 'Please select a vehicle' }]}
                  >
                    <Select
                      placeholder="Select vehicle"
                      disabled={!selectedCustomerId || isEditing}
                    >
                      {vehicles.map((vehicle) => (
                        <Option key={vehicle.id} value={vehicle.id}>
                          {vehicle.year} {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Work Order Details */}
          <Col xs={24}>
            <Card title="Work Order Details" className="mb-6">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true }]}
                    initialValue="new"
                  >
                    <Select>
                      <Option value="new">New</Option>
                      <Option value="in_progress">In Progress</Option>
                      <Option value="waiting_parts">Waiting Parts</Option>
                      <Option value="waiting_approval">Waiting Approval</Option>
                      <Option value="completed">Completed</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="priority"
                    label="Priority"
                    rules={[{ required: true }]}
                    initialValue="normal"
                  >
                    <Select>
                      <Option value="low">Low</Option>
                      <Option value="normal">Normal</Option>
                      <Option value="high">High</Option>
                      <Option value="urgent">Urgent</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="assigned_technician" label="Assigned Technician">
                    <Input placeholder="Enter technician name" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item name="estimated_completion" label="Estimated Completion">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="problem_description"
                label="Problem Description"
                rules={[{ required: true, message: 'Please describe the problem' }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Describe the customer's problem or requested service..."
                  showCount
                  maxLength={1000}
                />
              </Form.Item>

              <Form.Item name="work_performed" label="Work Performed">
                <TextArea
                  rows={4}
                  placeholder="Describe the work that was or will be performed..."
                  showCount
                  maxLength={1000}
                />
              </Form.Item>
            </Card>
          </Col>

          {/* Parts */}
          <Col xs={24}>
            <Card
              title="Parts"
              className="mb-6"
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={addPart} size={isMobile ? 'middle' : 'large'}>
                  Add Part
                </Button>
              }
            >
              {isMobile ? (
                parts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No parts added yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {parts.map((part, index) => (
                      <Card key={index} size="small" style={{ backgroundColor: '#fafafa' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <Input
                            value={part.part_name}
                            onChange={(e) => updatePart(index, 'part_name', e.target.value)}
                            placeholder="Part name"
                            size="large"
                          />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <Input
                              value={part.part_number}
                              onChange={(e) => updatePart(index, 'part_number', e.target.value)}
                              placeholder="Part #"
                            />
                            <Select
                              value={part.status}
                              onChange={(val) => updatePart(index, 'status', val)}
                              style={{ width: '100%' }}
                            >
                              <Option value="needed">Needed</Option>
                              <Option value="ordered">Ordered</Option>
                              <Option value="received">Received</Option>
                              <Option value="installed">Installed</Option>
                            </Select>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, alignItems: 'center' }}>
                            <InputNumber
                              value={part.quantity}
                              onChange={(val) => updatePart(index, 'quantity', val || 0)}
                              min={0}
                              style={{ width: '100%' }}
                              placeholder="Qty"
                            />
                            <InputNumber
                              value={part.unit_price}
                              onChange={(val) => updatePart(index, 'unit_price', val || 0)}
                              min={0}
                              formatter={(value) => `$${value}`}
                              parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
                              style={{ width: '100%' }}
                              placeholder="Price"
                            />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 600, color: '#22c55e' }}>${part.total_price.toFixed(2)}</span>
                              <Button type="text" icon={<DeleteOutlined />} onClick={() => removePart(index)} danger size="small" />
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )
              ) : (
                <Table
                  dataSource={parts}
                  columns={partsColumns}
                  pagination={false}
                  rowKey={(record, index) => index!}
                  locale={{ emptyText: 'No parts added yet' }}
                  scroll={{ x: 700 }}
                />
              )}
            </Card>
          </Col>

          {/* Labor */}
          <Col xs={24}>
            <Card
              title="Labor"
              className="mb-6"
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={addLabor} size={isMobile ? 'middle' : 'large'}>
                  Add Labor
                </Button>
              }
            >
              {isMobile ? (
                laborItems.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No labor items added yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {laborItems.map((labor, index) => (
                      <Card key={index} size="small" style={{ backgroundColor: '#fafafa' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <Input
                            value={labor.description}
                            onChange={(e) => updateLabor(index, 'description', e.target.value)}
                            placeholder="Labor description"
                            size="large"
                          />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, alignItems: 'center' }}>
                            <InputNumber
                              value={labor.hours}
                              onChange={(val) => updateLabor(index, 'hours', val || 0)}
                              min={0}
                              step={0.5}
                              style={{ width: '100%' }}
                              placeholder="Hours"
                            />
                            <InputNumber
                              value={labor.hourly_rate}
                              onChange={(val) => updateLabor(index, 'hourly_rate', val || 0)}
                              min={0}
                              formatter={(value) => `$${value}`}
                              parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ''))}
                              style={{ width: '100%' }}
                              placeholder="Rate"
                            />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 600, color: '#22c55e' }}>${labor.total_price.toFixed(2)}</span>
                              <Button type="text" icon={<DeleteOutlined />} onClick={() => removeLabor(index)} danger size="small" />
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )
              ) : (
                <Table
                  dataSource={laborItems}
                  columns={laborColumns}
                  pagination={false}
                  rowKey={(record, index) => index!}
                  locale={{ emptyText: 'No labor items added yet' }}
                  scroll={{ x: 600 }}
                />
              )}
            </Card>
          </Col>

          {/* Total */}
          <Col xs={24}>
            <Card className="mb-6">
              <div className="flex justify-between items-center text-xl font-semibold">
                <span>Total Amount:</span>
                <span className="text-green-600">
                  ${calculateTotalAmount().toFixed(2)}
                </span>
              </div>
            </Card>
          </Col>
        </Row>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'flex-end',
          gap: 12,
          paddingTop: 16,
          paddingBottom: isMobile ? 24 : 16,
          borderTop: '1px solid #f0f0f0',
        }}>
          <Button type="default" onClick={handleBack} size="large" disabled={loading} style={{ order: isMobile ? 2 : 1 }}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={loading}
            icon={<SaveOutlined />}
            size="large"
            style={{ fontWeight: 500, order: isMobile ? 1 : 2 }}
          >
            {loading ? 'Saving...' : isEditing ? 'Update Work Order' : 'Create Work Order'}
          </Button>
        </div>
      </Form>
    </div>
    </>
  );
};

export default WorkOrderForm;